import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  PipecatClientAudio,
  PipecatClientVideo,
  usePipecatClient,
  usePipecatClientTransportState,
  useRTVIClientEvent,
} from "@pipecat-ai/client-react";
import { RTVIEvent } from "@pipecat-ai/client-js";
import CallingScreen from './CallingScreen';

const VideoPlaceholder = ({ isInitiatingCall, setIsInitiatingCall, connectionError, onRetry }) => {
  const client = usePipecatClient();
  const transportState = usePipecatClientTransportState();
  const [videoDimensions, setVideoDimensions] = useState({});
  const [callTimedOut, setCallTimedOut] = useState(false);
  const [hasVideoTrack, setHasVideoTrack] = useState(false);
  const timeoutHandledRef = useRef(false);
  const videoTransitionTimeoutRef = useRef(null);
  const prevIsInitiatingCallRef = useRef(false);

  // Use isInitiatingCall prop for instant calling screen
  const isCalling = isInitiatingCall;

  // Reset states when user initiates a new call (only on false -> true transition)
  useEffect(() => {
    // Only reset when transitioning from false to true (new call starting)
    if (isInitiatingCall && !prevIsInitiatingCallRef.current) {
      console.log('New call initiated - resetting states');
      timeoutHandledRef.current = false;
      setCallTimedOut(false);
      setHasVideoTrack(false);
    }
    prevIsInitiatingCallRef.current = isInitiatingCall;
  }, [isInitiatingCall]);

  // Reset isInitiatingCall when connection completes or disconnects
  useRTVIClientEvent(
    RTVIEvent.TransportStateChanged,
    useCallback((state) => {
      console.log('Transport state changed:', state);

      // Hide calling screen when connected or ready (not just on disconnect)
      if ((state === 'connected' || state === 'ready') && setIsInitiatingCall) {
        console.log('Transport ready - hiding calling screen after brief delay');
        // Brief delay to ensure connection is stable
        setTimeout(() => {
          setIsInitiatingCall(false);
        }, 2000);
      }

      if (state === 'disconnected' && setIsInitiatingCall) {
        setIsInitiatingCall(false);
      }
    }, [setIsInitiatingCall])
  );

  // Wait for bot's VIDEO track to start (not just bot connected)
  useRTVIClientEvent(
    RTVIEvent.TrackStarted,
    useCallback((track, participant) => {
      console.log('=== TrackStarted Event ===');
      console.log('Track kind:', track?.kind);
      console.log('Participant:', participant);
      console.log('Participant name:', participant?.name);
      console.log('Participant local:', participant?.local);

      // Only switch when we get the bot's VIDEO track (participant is "Olivio" for bot)
      // Check if it's not a local track (local tracks have participant.local === true)
      const isLocalTrack = participant?.local === true;
      const isBotVideo = !isLocalTrack && track?.kind === 'video';

      console.log('isLocalTrack:', isLocalTrack, 'isBotVideo:', isBotVideo);
      console.log('Current state - isCalling:', isInitiatingCall, 'hasVideoTrack:', hasVideoTrack);

      if (isBotVideo) {
        console.log('✅ Bot video track started - video will show behind countdown overlay');

        // Clear any existing timeout to prevent duplicates
        if (videoTransitionTimeoutRef.current) {
          clearTimeout(videoTransitionTimeoutRef.current);
        }

        // Set hasVideoTrack to show the video and hide countdown after brief overlay
        videoTransitionTimeoutRef.current = setTimeout(() => {
          console.log('✅ Video showing - hiding countdown after brief overlay');
          setHasVideoTrack(true);

          // Hide countdown after video is confirmed visible (2 second overlay)
          setTimeout(() => {
            if (setIsInitiatingCall) {
              setIsInitiatingCall(false);
            }
          }, 2000);

          videoTransitionTimeoutRef.current = null;
        }, 500); // Short delay for smooth transition
      } else {
        console.log('❌ Not a bot video track - keeping calling screen');
      }
    }, [setIsInitiatingCall, isInitiatingCall, hasVideoTrack])
  );

  // Listen for disconnect to reset states
  useRTVIClientEvent(
    RTVIEvent.Disconnected,
    useCallback(() => {
      console.log('Disconnected - resetting state');

      // Clear any pending video transition timeout
      if (videoTransitionTimeoutRef.current) {
        clearTimeout(videoTransitionTimeoutRef.current);
        videoTransitionTimeoutRef.current = null;
      }

      if (setIsInitiatingCall) {
        setIsInitiatingCall(false);
      }
      setCallTimedOut(false);
      setHasVideoTrack(false);
    }, [setIsInitiatingCall])
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending video transition timeout
      if (videoTransitionTimeoutRef.current) {
        clearTimeout(videoTransitionTimeoutRef.current);
      }
    };
  }, []);

  const handleTimeout = useCallback(async () => {
    // Prevent multiple timeout calls
    if (timeoutHandledRef.current) {
      console.log('Timeout already handled, ignoring duplicate call');
      return;
    }
    timeoutHandledRef.current = true;

    console.log('Call timed out after 1 minute');

    // Update state immediately
    setCallTimedOut(true);
    if (setIsInitiatingCall) {
      setIsInitiatingCall(false);
    }

    // Disconnect the client if still connected (do this after state update)
    setTimeout(async () => {
      if (client) {
        try {
          console.log('Disconnecting client due to timeout');
          await client.disconnect();
        } catch (error) {
          console.error('Error disconnecting client after timeout:', error);
        }
      }
    }, 100);
  }, [client, setIsInitiatingCall]);

  const isConnected = transportState !== "disconnected";
  // Show video when connected and has video track, even if calling overlay is active
  const showVideo = isConnected && hasVideoTrack;
  const hasError = callTimedOut || connectionError;

  // Log what component will be rendered (only errors and video)
  if (hasError) {
    console.log('⚠️ Will render Error screen');
  } 

  // Determine error message
  const getErrorContent = () => {
    if (connectionError) {
      return {
        icon: 'fa-exclamation-circle',
        iconColor: '#ef4444',
        title: 'Server Unavailable',
        message: 'Our servers are currently experiencing issues. Please try again in a few moments.'
      };
    } else if (callTimedOut) {
      return {
        icon: 'fa-clock',
        iconColor: '#f59e0b',
        title: 'Connection Timeout',
        message: 'We\'re experiencing high traffic or network issues. Please try again.'
      };
    }
    return null;
  };

  const errorContent = getErrorContent();

  return (
    <div className="popup-video-section">
      <div className="video-container-popup" style={{ width: '100%', height: '100%', position: 'relative', overflow: 'visible' }}>
        {/* Main content */}
        {hasError ? (
          <div className="video-placeholder-content">
            <style>{`
              .error-container {
                text-align: center;
                color: white;
                animation: fadeInUp 0.5s ease-out;
              }

              @keyframes fadeInUp {
                from {
                  opacity: 0;
                  transform: translateY(20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }

              .error-icon-wrapper {
                width: 130px;
                height: 130px;
                background: radial-gradient(circle,
                  rgba(139, 92, 246, 0.15) 0%,
                  rgba(124, 58, 237, 0.08) 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto 2rem;
                border: 2px solid rgba(139, 92, 246, 0.25);
                box-shadow: 0 10px 40px rgba(139, 92, 246, 0.2),
                            0 0 60px rgba(139, 92, 246, 0.1) inset;
                position: relative;
                animation: pulse 2s ease-in-out infinite;
              }

              @keyframes pulse {
                0%, 100% {
                  transform: scale(1);
                  box-shadow: 0 10px 40px rgba(139, 92, 246, 0.2),
                              0 0 60px rgba(139, 92, 246, 0.1) inset;
                }
                50% {
                  transform: scale(1.05);
                  box-shadow: 0 15px 50px rgba(139, 92, 246, 0.3),
                              0 0 80px rgba(139, 92, 246, 0.15) inset;
                }
              }

              .error-title {
                margin-bottom: 0.75rem;
                font-size: 1.6rem;
                font-weight: 700;
                color: #ffffff;
                letter-spacing: -0.3px;
              }

              .error-message {
                color: rgba(255, 255, 255, 0.7);
                fontSize: 1.05rem;
                line-height: 1.6;
                margin-bottom: 2rem;
                max-width: 420px;
                margin-left: auto;
                margin-right: auto;
              }

              .retry-hint {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
                color: #a78bfa;
                font-size: 0.95rem;
                font-weight: 600;
                padding: 12px 24px;
                background: rgba(139, 92, 246, 0.1);
                border-radius: 8px;
                border: 1px solid rgba(139, 92, 246, 0.2);
                cursor: pointer;
                transition: all 0.3s ease;
              }

              .retry-hint:hover {
                background: rgba(139, 92, 246, 0.2);
                border-color: rgba(139, 92, 246, 0.4);
                color: #c4b5fd;
                transform: translateY(-2px);
                box-shadow: 0 4px 16px rgba(139, 92, 246, 0.3);
              }

              .retry-hint:active {
                transform: translateY(0);
              }
            `}</style>
            <div className="error-container">
              <div className="error-icon-wrapper">
                <i className={`fas ${errorContent?.icon}`} style={{ fontSize: '3.8rem', color: errorContent?.iconColor }}></i>
              </div>
              <h3 className="error-title">
                {errorContent?.title}
              </h3>
              <p className="error-message">
                {errorContent?.message}
              </p>
              <div className="retry-hint" onClick={() => onRetry && onRetry()}>
                <i className="fas fa-redo"></i>
                Click here to try again
              </div>
            </div>
          </div>
        ) : showVideo ? (
          <>
            <PipecatClientVideo
              participant="bot"
              fit="cover"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <PipecatClientAudio />
          </>
        ) : (
          <div className="video-placeholder-content">
            {/* Empty placeholder before calling */}
          </div>
        )}

        {/* Countdown overlay - renders last to be on top in DOM */}
        {isCalling && (
          <CallingScreen onTimeout={handleTimeout} />
        )}
      </div>
    </div>
  );
};

export default VideoPlaceholder;